import ScienceIcon from "@mui/icons-material/Science";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, CircularProgress, Typography } from "@mui/material";
import { BORDER_RADIUS } from "app/theme";
import { useMemo } from "react";
import type { MilkQualityPerFarmerPeriodData } from "../hooks/useInsightsDashboardQueries";
import {
  SCC_THRESHOLDS,
  TBC_THRESHOLDS,
  ZONE_COLORS,
} from "../milkQualityThresholds";

function getLatestPerFarmer(
  rows: MilkQualityPerFarmerPeriodData[]
): Map<string, MilkQualityPerFarmerPeriodData> {
  const latest = new Map<string, MilkQualityPerFarmerPeriodData>();
  for (const r of rows) {
    const uid = r["MilkQuality.userGuid"];
    const existing = latest.get(uid);
    if (
      !existing ||
      r["MilkQuality.periodStart"] > existing["MilkQuality.periodStart"]
    ) {
      latest.set(uid, r);
    }
  }
  return latest;
}

function AlertChip({
  icon,
  count,
  label,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
}) {
  const isActive = count > 0;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        borderRadius: `${BORDER_RADIUS.small}px`,
        backgroundColor: isActive ? `${ZONE_COLORS.red}12` : "#fafafa",
        border: `1px solid ${isActive ? `${ZONE_COLORS.red}30` : "#f0f0f0"}`,
      }}
    >
      <Box sx={{ color: isActive ? ZONE_COLORS.red : "#a1a1aa" }}>{icon}</Box>
      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: isActive ? ZONE_COLORS.red : "#71717a",
            fontSize: "1.1rem",
          }}
        >
          {count}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: isActive ? "#71717a" : "#a1a1aa", lineHeight: 1.2 }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

interface MilkQualityAlertCardProps {
  data: MilkQualityPerFarmerPeriodData[] | undefined;
  isLoading: boolean;
}

export default function MilkQualityAlertCard({
  data,
  isLoading,
}: MilkQualityAlertCardProps) {
  const alerts = useMemo(() => {
    if (!data) return { scc: 0, tbc: 0, antibiotic: 0 };
    const latestMap = getLatestPerFarmer(data);
    let scc = 0;
    let tbc = 0;
    let antibiotic = 0;
    for (const r of latestMap.values()) {
      const sccVal = Number(r["MilkQuality.somaticCellCount"]);
      const tbcVal = Number(r["MilkQuality.totalBacterialCount"]);
      const abTest = r["MilkQuality.antibioticTest"];
      if (sccVal >= SCC_THRESHOLDS.warning) scc++;
      if (tbcVal >= TBC_THRESHOLDS.warning) tbc++;
      if (abTest && abTest.toLowerCase() !== "pass") antibiotic++;
    }
    return { scc, tbc, antibiotic };
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
        gap: 1.5,
        mb: 2,
      }}
    >
      <AlertChip
        icon={<WarningAmberIcon fontSize="small" />}
        count={alerts.scc}
        label="above SCC limit (400k)"
      />
      <AlertChip
        icon={<WarningAmberIcon fontSize="small" />}
        count={alerts.tbc}
        label="above TBC limit (100k)"
      />
      <AlertChip
        icon={<ScienceIcon fontSize="small" />}
        count={alerts.antibiotic}
        label="failed antibiotic tests"
      />
    </Box>
  );
}
